import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, PartyPopper } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { WizardStepper } from './WizardStepper';
import { FundPicker } from './FundPicker';
import { UttLinks } from './StepGuide';
import { AccountLink } from './AccountLink';
import { FundAccount } from './FundAccount';
import { ApplicationForm } from './ApplicationForm';
import { AwaitingUTT } from './AwaitingUTT';
import { WIZARD_STAGES, stageIndex } from './uttSteps';
import { savePdf } from './savePdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SW_ERRORS = {
  'NIDA number must have 20 digits': 'Namba ya NIDA lazima iwe na tarakimu 20',
  'Enter a valid Tanzanian mobile number': 'Weka namba sahihi ya simu ya Tanzania',
  'Please confirm consent': 'Tafadhali thibitisha ridhaa ya kushiriki taarifa zako na UTT AMIS',
  'Enter your full name': 'Weka jina lako kamili',
  'Enter your email address': 'Weka barua pepe yako',
  'value is not a valid email': 'Barua pepe si sahihi',
  'Account number may contain only': 'Namba ya akaunti iwe na herufi, tarakimu, "-" au "/" tu',
  'Applicant must be 18+': 'Mwombaji lazima awe na miaka 18 au zaidi (tumia Mfuko wa Watoto kwa watoto)',
  'Date of birth must be': 'Tarehe ya kuzaliwa iwe katika muundo MMMM-MM-SS',
  'Choose a UTT fund first': 'Chagua mfuko wa UTT kwanza',
  'Your UTT account is already registered': 'Akaunti yako ya UTT tayari imesajiliwa',
};

export const UTTOnboardingWizard = ({ funds }) => {
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const [state, setState] = useState(null);
  const [application, setApplication] = useState(null);
  const [view, setView] = useState(0);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const err = (e, fallback) => {
    const d = e.response?.data?.detail;
    const msg = (Array.isArray(d) ? d[0]?.msg : typeof d === 'string' ? d : null) || fallback;
    const swKey = Object.keys(SW_ERRORS).find((k) => msg.startsWith(k));
    toast.error(language === 'sw' && swKey ? SW_ERRORS[swKey] : msg);
  };

  const load = useCallback(async (jump = false) => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    const [s, a] = await Promise.all([axios.get(`${API}/utt-onboarding`, h), axios.get(`${API}/utt-onboarding/application`, h)]);
    setState(s.data);
    setApplication(a.data.application);
    if (jump) setView(stageIndex(s.data.stage));
  }, [token]);

  useEffect(() => { load(true).catch(() => toast.error('Could not load your UTT progress')); }, [load]);

  const chooseFund = async (id) => {
    try {
      const res = await axios.put(`${API}/utt-onboarding`, { fund_id: id }, auth);
      setState((s) => ({ ...s, ...res.data }));
    } catch (e) { err(e, 'Update failed'); }
  };

  const submitApplication = async (body) => {
    try {
      const res = await axios.put(`${API}/utt-onboarding/application`, body, auth);
      setApplication(res.data);
      await load(false);
      setView(stageIndex('awaiting'));
      toast.success(t('Application sent to UTT AMIS', 'Ombi limetumwa UTT AMIS'));
    } catch (e) { err(e, 'Could not submit'); }
  };

  const blobErr = async (e, fallback) => {
    let data = {};
    try { data = JSON.parse(await e.response?.data?.text()); } catch { /* not JSON */ }
    err({ response: { data } }, fallback);
  };

  const previewApplication = async (body) => {
    try {
      const r = await axios.post(`${API}/utt-onboarding/application/preview`, body, { ...auth, responseType: 'blob' });
      savePdf(r.data, 'UTT-form-preview.pdf');
    } catch (e) { await blobErr(e, 'Could not build the preview'); }
  };

  const downloadApplication = async () => {
    try {
      const r = await axios.get(`${API}/utt-onboarding/application/pdf`, { ...auth, responseType: 'blob' });
      savePdf(r.data, `${application?.ref || 'UTT'}-UTT-form.pdf`);
    } catch (e) { await blobErr(e, 'Could not download your form'); }
  };

  const saveAccount = async (acct) => {
    try {
      await axios.put(`${API}/utt-onboarding/account`, { utt_account_number: acct }, auth);
      await load(false);
      setView(stageIndex('fund_account'));
      toast.success(t('UTT account linked', 'Akaunti ya UTT imeunganishwa'));
    } catch (e) { err(e, 'Could not save'); }
  };

  if (!state) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const maxReached = stageIndex(state.stage);
  const fund = funds.find((f) => f.id === state.fund_id);
  const stageId = WIZARD_STAGES[view].id;
  const prefill = { full_name: user?.name || '', phone: user?.phone || '', email: user?.email || '', ...(application?.fields || {}) };

  return (
    <Card className="border-border/60" data-testid="utt-onboarding-wizard">
      <CardContent className="space-y-6 p-5 pb-28 sm:p-6 lg:pb-6">
        <div>
          <h2 className="text-xl font-bold">{t('Open & fund your UTT AMIS account', 'Fungua na weka pesa kwenye akaunti ya UTT AMIS')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('One form — we hand it to UTT AMIS for you. Progress is saved.', 'Fomu moja — tunaikabidhi UTT AMIS kwa niaba yako. Maendeleo yanahifadhiwa.')}</p>
        </div>
        <WizardStepper current={view} maxReached={maxReached} onSelect={setView} language={language} />

        {stageId === 'fund' && (
          <div>
            <FundPicker funds={funds} selected={state.fund_id} onSelect={chooseFund} />
            <Button onClick={() => setView(1)} disabled={!state.fund_id} className="mt-4" data-testid="utt-next-fund">
              {t('Continue', 'Endelea')}<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
        {stageId === 'apply' && <ApplicationForm key={application?.ref || 'new'} initial={prefill} onSubmit={submitApplication} onPreview={previewApplication} hasPhoto={!!application?.has_photo} />}
        {stageId === 'awaiting' && (
          application
            ? <AwaitingUTT application={application} onEdit={() => setView(1)} onLinkAccount={saveAccount} onDownload={downloadApplication} />
            : <AccountLink masked={state.utt_account_masked} onSave={saveAccount} />
        )}
        {stageId === 'fund_account' && (
          state.has_account ? (
            <div className="space-y-4">
              {state.stage === 'allocated' && (
                <div className="flex items-center gap-2 rounded-2xl bg-primary/10 p-3 text-sm text-primary" data-testid="utt-allocated-banner">
                  <PartyPopper className="h-4 w-4" />{t('Your units are allocated and live in your portfolio.', 'Vipande vyako vimegawiwa na viko kwenye portfolio yako.')}
                  <Link to="/portfolio/projection" className="ml-auto font-semibold underline" data-testid="utt-view-portfolio">{t('View portfolio', 'Ona portfolio')}</Link>
                </div>
              )}
              <p className="text-sm" data-testid="utt-account-ready">{t(`UTT account ${state.utt_account_masked} is ready.`, `Akaunti ya UTT ${state.utt_account_masked} iko tayari.`)}</p>
              <FundAccount fund={fund} orders={state.orders || []} defaultPhone={user?.phone} token={token} onChange={() => load(false)} />
              {application && <button type="button" onClick={downloadApplication} className="text-xs font-medium text-primary underline" data-testid="utt-download-form-registered">
                {t('Download my UTT application form', 'Pakua fomu yangu ya maombi ya UTT')}</button>}
              <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">{t('UTT AMIS app & help', 'App ya UTT AMIS na msaada')}</summary><div className="mt-2"><UttLinks /></div></details>
            </div>
          ) : <p className="text-sm text-muted-foreground">{t('Available once UTT AMIS registers your account.', 'Itapatikana UTT AMIS ikishasajili akaunti yako.')}</p>
        )}
      </CardContent>
    </Card>
  );
};
